import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import {
  Modal,
  Button,
  Input,
  Label,
  MultiSelect,
} from '@atrvd/saas-ui'
import { useUpdatePack, useFacilities } from '@/hooks/useBanking'
import { packFormSchema, type PackFormData } from '@/lib/pack-form'
import type { Pack } from '@/types/banking'

/**
 * Props for {@link EditPackDialog}.
 *
 * @property open         - Controls dialog visibility.
 * @property onOpenChange - Called when the dialog should open/close.
 * @property pack         - The pack being edited; `null` while no row is selected.
 */
interface EditPackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  pack: Pack | null
}

/**
 * Modal dialog for editing an existing Pack from the Settings → Packs page (DCP-171).
 *
 * Mirrors EditFacilityDialog's reset logic — a `prevIdRef` prevents a React
 * Query background-refetch from wiping in-progress edits. The form seeds from
 * the pack prop only when the dialog transitions to open or when a different
 * pack id is passed while the dialog is already open.
 *
 * Facility MultiSelect options = unassigned facilities (pack_id === null) PLUS
 * the current pack's own members (pack_id === pack.id). This ensures the pack's
 * existing facilities remain visible and toggleable while preventing a facility
 * that belongs to a *different* pack from appearing (AC #4).
 *
 * @param props - See {@link EditPackDialogProps}.
 */
export function EditPackDialog({ open, onOpenChange, pack }: EditPackDialogProps) {
  const updatePack = useUpdatePack()
  // DCP-292: fetch ALL facilities (activeOnly:false). A pack member that has
  // since been DEACTIVATED must still be seeded as selected AND offered in the
  // options — otherwise saving the pack would silently drop it from membership.
  // The options memo below excludes deactivated UNASSIGNED facilities so they
  // can't be newly added; only the pack's own (possibly inactive) members stay.
  const { data: facilities = [], isLoading: facilitiesLoading } = useFacilities({ activeOnly: false })

  /**
   * Last server-side mutation error, surfaced inline above the form so it
   * stays visible after the transient toast fades. Cleared on next submit
   * and on dialog close.
   */
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PackFormData>({
    resolver: zodResolver(packFormSchema),
  })

  /**
   * Track the previous pack id so a parent re-render that mints a fresh `pack`
   * object (same row, new reference — e.g. React Query refetch while the dialog
   * is open) doesn't wipe the user's in-progress edits.
   *
   * Reset only fires on:
   *   1. closed → open transition (new edit session).
   *   2. A different pack.id arrives while the dialog is open.
   *
   * Cleared on close so the next open is treated as a fresh session.
   */
  const prevPackIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!open) {
      // Clear the ref on close so the next open re-seeds from the fresh snapshot.
      prevPackIdRef.current = null
      return
    }
    // Wait until facilities have loaded before seeding — otherwise the pack's
    // current members can't be derived and the form would open with an empty
    // selection. A background refetch afterwards does NOT re-seed: the
    // `prevPackIdRef` guard fires only on a new pack.id, so in-progress edits
    // are preserved.
    if (!pack || facilitiesLoading) return
    if (pack.id === prevPackIdRef.current) return

    const currentMemberIds = facilities
      .filter((f) => f.pack_id === pack.id)
      .map((f) => f.id)

    reset({ name: pack.name, facility_ids: currentMemberIds })
    prevPackIdRef.current = pack.id
    setServerError(null)
  }, [pack, open, reset, facilities, facilitiesLoading])

  /** Close the dialog and reset form state. */
  const handleClose = () => {
    reset()
    setServerError(null)
    onOpenChange(false)
  }

  /**
   * Handle form submission. On success shows a toast and closes the dialog;
   * on failure surfaces the server message inline and via toast.
   *
   * @param values - Validated form values produced by Zod.
   */
  const onSubmit = async (values: PackFormData) => {
    if (!pack) return

    setServerError(null)
    try {
      await updatePack.mutateAsync({
        id: pack.id,
        data: {
          name: values.name,
          facility_ids: values.facility_ids,
        },
      })
      toast.success('Pack updated')
      handleClose()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update pack'
      setServerError(errorMessage)
      toast.error(errorMessage)
    }
  }

  /**
   * Facilities eligible for selection in the Edit form:
   *   - ACTIVE facilities with `pack_id === null` (unassigned — free to move to
   *     any pack). DCP-292: a DEACTIVATED unassigned facility is NOT offered as
   *     a new addition.
   *   - Facilities with `pack_id === pack.id` (already in this pack — must stay
   *     selectable even if deactivated, so saving doesn't drop them; flagged
   *     "(inactive)").
   *
   * Facilities belonging to a *different* pack are excluded (AC #4).
   */
  const eligibleOptions = useMemo(
    () =>
      facilities
        .filter((f) => (f.pack_id === null && f.is_active) || f.pack_id === pack?.id)
        .map((f) => ({ value: f.id, label: f.is_active ? f.name : `${f.name} (inactive)` })),
    [facilities, pack?.id],
  )

  const watchedFacilityIds = watch('facility_ids')

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Edit Pack"
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={updatePack.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-pack-form"
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={updatePack.isPending}
          >
            {updatePack.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Update
          </Button>
        </div>
      }
    >
      <form
        id="edit-pack-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 px-1"
      >
        {/*
          Inline server-error block — sibling to the per-field Zod errors
          below. Stays visible until the next submit or dialog close,
          unlike the transient toast.
        */}
        {serverError && (
          <div
            role="alert"
            className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
          >
            {serverError}
          </div>
        )}

        {/* Pack Name */}
        <div className="space-y-2">
          <Label htmlFor="edit-pack-name">
            Pack Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="edit-pack-name"
            placeholder="Enter pack name"
            disabled={updatePack.isPending}
            {...register('name')}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        {/* Facilities MultiSelect */}
        <div className="space-y-2">
          <Label>
            Facilities <span className="text-destructive">*</span>
          </Label>
          {/*
            MultiSelect is not a native input, so it cannot be wired via
            register(). Instead we read the current value from watch() and
            push updates via setValue(). The `shouldValidate: true` flag
            ensures Zod re-validates the field immediately on change so the
            "Select at least one facility" error clears without waiting for
            the next submit.
          */}
          <MultiSelect
            options={eligibleOptions}
            value={watchedFacilityIds ?? []}
            onChange={(v) => setValue('facility_ids', v, { shouldValidate: true })}
            placeholder="Select facilities"
            searchable
            disabled={updatePack.isPending}
          />
          {errors.facility_ids && (
            <p className="text-sm text-destructive">{errors.facility_ids.message}</p>
          )}
        </div>
      </form>
    </Modal>
  )
}
